/**
 * IPAMSubnets — /networks/subnets
 * All subnets across all blocks, filterable by block/type/site; CRUD.
 * Reads optional ?blockId= from URL to pre-filter.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BuildingOffice2Icon,
  Cog6ToothIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  ServerStackIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import IpamCascadeDeleteModal from './IpamCascadeDeleteModal';
import IpamVlanQuickCreate from './IpamVlanQuickCreate';
import IpamSubnetTypesManager from './IpamSubnetTypesManager';
import ListPage from './common/ListPage';
import LocationTreeSelect from './common/LocationTreeSelect';
import api from '../services/api';
import useLocationPaths from '../hooks/useLocationPaths';
import { SUBNET_TYPE_COLORS, subnetTypeLabel, useSubnetTypes } from '../utils/ipamTypes';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';

/* Ocupación con los colores del sistema de diseño (nada de hex a mano). */
function occColor(pct) {
  if (pct >= 90) return 'var(--red-600)';
  if (pct >= 70) return 'var(--amber-600)';
  return 'var(--green-600)';
}

/* El catálogo de tipos guarda colores de Bootstrap: se traducen a los badges
   del estándar de listados para que todo se vea igual. */
function typeBadgeClass(value) {
  const color = SUBNET_TYPE_COLORS[value] || 'secondary';
  if (color === 'success' || color === 'primary') return 'list-badge list-badge--on';
  if (color === 'warning' || color === 'info') return 'list-badge list-badge--warn';
  if (color === 'danger') return 'list-badge list-badge--off';
  return 'list-badge';
}

/* Estados reales en base de datos. «Agotada» no se guarda: se deduce de la
   ocupación al 100 % y se ofrece como un valor más del filtro. */
const SUBNET_STATUS_META = {
  active:     { label: 'Activa',      badge: 'list-badge--on' },
  reserved:   { label: 'Planificada', badge: 'list-badge--warn' },
  deprecated: { label: 'Retirada',    badge: 'list-badge--off' },
};

const OCCUPATION_RANGES = {
  low:  { label: '< 50 %',    test: (pct) => pct > 0 && pct < 50 },
  mid:  { label: '50 – 80 %', test: (pct) => pct >= 50 && pct <= 80 },
  high: { label: '> 80 %',    test: (pct) => pct > 80 },
  none: { label: 'Sin uso',   test: (pct) => !pct },
};

export default function IPAMSubnets({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initBlock = searchParams.get('blockId') || '';
  const initVlan  = searchParams.get('vlanId') || '';
  const initCidr  = searchParams.get('cidr') || '';
  const [hlId, setHlId] = useState(null);
  const [delegatedCompany, setDelegatedCompany] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const errTimer = useRef(null);
  const okTimer  = useRef(null);

  const [subnets, setSubnets] = useState([]);
  const [blocks, setBlocks]   = useState([]);
  const [vlans, setVlans]     = useState([]);
  const [companies, setCompanies] = useState([]); // for delegation dropdown

  // Filters
  const [filterBlock, setFilterBlock] = useState(initBlock);
  const [filterType, setFilterType]   = useState('');
  const [filterSite, setFilterSite]   = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterVersion, setFilterVersion] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVlan, setFilterVlan]   = useState(initVlan);
  const [filterDelegation, setFilterDelegation] = useState('');
  const [filterOccupation, setFilterOccupation] = useState('');
  const [filterGateway, setFilterGateway] = useState(false);   // solo con gateway
  const [filterDhcp, setFilterDhcp]   = useState(false);       // solo con DHCP
  const [search, setSearch]           = useState(initCidr);

  // Rangos DHCP: dan la señal «esta subred tiene DHCP» y la acción de salto.
  const [dhcpRanges, setDhcpRanges]   = useState([]);

  // Ordenación y agrupación del listado
  const [grouped, setGrouped] = useState(true);
  // Sitios vistos alguna vez: el desplegable no se queda sin opciones al filtrar.
  const [knownSites, setKnownSites] = useState([]);

  // Catálogo de tipos (editable) — botón «Tipos»
  const { types: subnetTypes, reload: reloadSubnetTypes } = useSubnetTypes(companyId);
  const [typesOpen, setTypesOpen] = useState(false);

  // Árbol de ubicaciones: la columna y el filtro pintan la ruta completa
  // («Sede › Planta 2 › Sala 3»), no solo el nombre.
  const { locations: locationOptions, pathFor } = useLocationPaths(companyId);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitial, setWizardInitial] = useState(null);   // copia de subred

  // Edit modal
  const [editingSubnet, setEditingSubnet] = useState(null); // subnet object or null
  const [editSaving, setEditSaving]       = useState(false);

  // Borrado en cascada
  const [deleteTarget, setDeleteTarget] = useState(null); // { subnet, preview }
  const [deleteError, setDeleteError]   = useState('');
  const [deleting, setDeleting]         = useState(false);

  // Alta de VLAN desde el propio formulario de subred: se añade a la lista
  // local para que quede seleccionable al instante, sin recargar la pantalla.
  const handleVlanCreated = useCallback((vlan) => {
    if (!vlan?.id) return;
    setVlans(prev => (prev.some(v => v.id === vlan.id) ? prev : [...prev, vlan]));
  }, []);

  const handleUpdateSubnet = async (data) => {
    if (!editingSubnet) return;
    setEditSaving(true);
    try {
      await api.updateIpamSubnet(editingSubnet.id, data, companyId);
      setEditingSubnet(null);
      load();
    } catch (e) {
      setError(e.message || 'Error actualizando subred');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteSubnet = async (subnet) => {
    setDeleteError('');    try {
      const res = await api.getIpamSubnetDeletePreview(subnet.id, companyId);
      setDeleteTarget({ subnet, preview: res.data });
    } catch (e) {
      setError(e.message || 'Error consultando el contenido de la subred');
    }
  };

  const confirmDeleteSubnet = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await api.deleteIpamSubnet(deleteTarget.subnet.id, companyId);
      const counts = res?.data?.counts || {};
      setSuccess(
        `Subred ${deleteTarget.subnet.cidr} eliminada: ${counts.ips || 0} IP(s)` +
        (counts.elements ? ` · ${counts.elements} elemento(s) desasociado(s).` : '.')
      );
      setDeleteTarget(null);
      load();
    } catch (e) {
      setDeleteError(e.message || 'Error eliminando subred');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  useEffect(() => {
    if (success) {
      clearTimeout(okTimer.current);
      okTimer.current = setTimeout(() => setSuccess(''), 9000);
    }
    return () => clearTimeout(okTimer.current);
  }, [success]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const filters = {};
      if (filterBlock) filters.block_id = filterBlock;
      if (filterType)  filters.type     = filterType;
      if (filterSite)  filters.site     = filterSite;
      const [s, b, v, r] = await Promise.all([
        api.getIpamSubnets(companyId, filters),
        api.getIpamBlocks(companyId),
        api.getIpamVlans(companyId),
        api.getIpamRanges(companyId, { type: 'dhcp' }).catch(() => ({ data: [] })),
      ]);
      setSubnets(s.data || []);
      setBlocks(b.data  || []);
      setVlans(v.data   || []);
      setDhcpRanges(r.data || []);
      setKnownSites(prev => {
        const merged = new Set(prev);
        (s.data || []).forEach(row => { if (row.site) merged.add(row.site); });
        return [...merged].sort((a, b) => compareText(a, b));
      });    } catch (e) {
      setError(e.message || 'Error cargando subredes');
    } finally {
      setLoading(false);
    }
  }, [companyId, filterBlock, filterType, filterSite]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB para llegar desde otra pantalla. */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !subnets.length) return undefined;
    const id = Number(raw);
    const target = subnets.find(s => s.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setEditingSubnet(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`subnet-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, subnets, setSearchParams]);

  /* ?new=1 — el botón «Añadir subred» de la lista de bloques entra directamente
     al asistente, con el bloque ya seleccionado. */
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setWizardOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('new');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load companies once (for delegation dropdown)
  useEffect(() => {
    api.getAuthorizedCompanies().then(setCompanies).catch(() => {});
  }, []);

  const blockMap = Object.fromEntries(blocks.map(b => [String(b.id), b.cidr]));

  /** Subredes que tienen al menos un rango DHCP (para el filtro y el botón). */
  const dhcpSubnetIds = useMemo(
    () => new Set(dhcpRanges.map(r => String(r.subnet_id)).filter(Boolean)),
    [dhcpRanges],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const range = OCCUPATION_RANGES[filterOccupation];
    return subnets.filter(s => {
      if (filterVersion && String(s.version) !== filterVersion) return false;
      if (filterStatus === 'full') {
        if (!s.usable_ips || (s.occupation_pct ?? 0) < 100) return false;
      } else if (filterStatus && (s.status || 'active') !== filterStatus) {
        return false;
      }
      if (filterVlan && String(s.vlan_id ?? '') !== filterVlan) return false;
      if (filterDelegation === 'mine' && s.viewer_is_owner === false) return false;
      if (filterDelegation === 'out' && !s.delegated_to_company_name) return false;
      if (filterDelegation === 'in' && s.viewer_is_owner !== false) return false;
      if (range && !range.test(s.occupation_pct || 0)) return false;
      if (filterGateway && !s.gateway) return false;
      if (filterDhcp && !dhcpSubnetIds.has(String(s.id))) return false;
      if (filterLocation) {
        const id = s.location_id != null ? String(s.location_id) : 'none';
        if (id !== filterLocation) return false;
      }
      if (!needle) return true;
      return s.cidr.includes(needle)
        || (s.name || '').toLowerCase().includes(needle)
        || (s.site || '').toLowerCase().includes(needle)
        || (s.location_name || '').toLowerCase().includes(needle)
        || (s.description || '').toLowerCase().includes(needle)
        || (s.gateway || '').toLowerCase().includes(needle)
        || (blockMap[String(s.block_id)] || '').toLowerCase().includes(needle);
    });
  }, [subnets, search, filterVersion, filterStatus, filterVlan, filterDelegation,
      filterOccupation, filterGateway, filterDhcp, dhcpSubnetIds, blockMap,
      filterLocation]);

  // Tipos del catálogo más los que aparezcan en los datos sin estar en él
  // (datos antiguos o creados por API se siguen pudiendo filtrar).
  const typeOptions = useMemo(() => {
    const catalogValues = subnetTypes.map(t => t.value);
    const extra = [...new Set(subnets.map(s => s.type).filter(Boolean))]
      .filter(value => !catalogValues.includes(value));
    return [...catalogValues, ...extra];
  }, [subnetTypes, subnets]);

  const allSites = knownSites;

  /** «Ver empresa delegada»: ficha mínima de la empresa a la que se delegó. */
  const showDelegatedCompany = useCallback((subnet) => {
    const match = companies.find(c => String(c.id) === String(subnet.delegated_to_company_id));
    setDelegatedCompany({
      name: subnet.delegated_to_company_name || match?.name || `#${subnet.delegated_to_company_id}`,
      id: subnet.delegated_to_company_id,
      slug: match?.slug,
      domain: match?.domain,
      subnet: subnet.cidr,
    });
  }, [companies]);

  /** Etiqueta del grupo cuando se agrupa por sitio. Si el toggle está apagado
      no se pasa `groupBy` (devolver `null` creaba un grupo «Sin valor»). */
  const groupLabelOf = useCallback(
    (subnet) => subnet.site || tr('ipam.no_site', 'Sin site'),
    [tr],
  );

  /** «Copiar subred»: abre el asistente con el bloque y los detalles ya puestos;
      lo único que hay que elegir es el CIDR nuevo (los CIDR son únicos). */
  const openCopy = useCallback((subnet) => {
    setWizardInitial({
      blockId: subnet.block_id != null ? String(subnet.block_id) : '',
      type: subnet.type || 'lan',
      vlanId: subnet.vlan_id != null ? String(subnet.vlan_id) : '',
      site: subnet.site || '',
      locationId: subnet.location_id != null ? String(subnet.location_id) : '',
      name: subnet.name ? `${subnet.name} (copia)` : '',
      description: subnet.description || '',
      delegateTo: subnet.delegated_to_company_id != null ? String(subnet.delegated_to_company_id) : '',
    });
    setWizardOpen(true);
  }, []);

  /* ── Columnas (catálogo completo; 8 visibles de entrada) ──────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'cidr',
        label: 'CIDR',
        sortable: true,
        render: (s) => <span className="list-code" style={{ fontWeight: 700 }}>{s.cidr}</span>,
      },
      {
        key: 'name',
        label: tr('ipam.name', 'Nombre'),
        sortable: true,
        render: (s) => <span style={{ fontWeight: 600 }}>{s.name || dash}</span>,
      },
      {
        key: 'block_id',
        label: tr('ipam.block', 'Bloque'),
        sortable: true,
        sortValue: (s) => blockMap[String(s.block_id)] || '',
        render: (s) => (s.block_id
          ? <span className="list-code">{blockMap[String(s.block_id)] || `#${s.block_id}`}</span>
          : dash),
      },
      {
        key: 'type',
        label: tr('ipam.type', 'Tipo'),
        sortable: true,
        sortValue: (s) => subnetTypeLabel(s.type),
        render: (s) => <span className={typeBadgeClass(s.type)}>{subnetTypeLabel(s.type)}</span>,
      },
      {
        key: 'version',
        label: tr('ipam.version', 'Versión'),
        sortable: true,
        sortValue: (s) => s.version || 4,
        render: (s) => <span className="list-badge list-code">v{s.version || 4}</span>,
      },
      {
        key: 'vlan_number',
        label: 'VLAN',
        sortable: true,
        sortValue: (s) => s.vlan_number ?? -1,
        render: (s) => (s.vlan_number != null
          ? <span className="list-badge">VLAN {s.vlan_number}</span>
          : dash),
      },
      {
        key: 'gateway',
        label: 'Gateway',
        sortable: true,
        render: (s) => (s.gateway ? <span className="list-code">{s.gateway}</span> : dash),
      },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (s) => s.site || '',
        render: (s) => s.site || dash,
      },
      {
        key: 'location',
        label: tr('ipam.location', 'Ubicación'),
        sortable: true,
        sortValue: (s) => pathFor(s.location_id) || s.location_name || '',
        render: (s) => {
          const ruta = pathFor(s.location_id) || s.location_name;
          if (!ruta) return dash;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span aria-hidden="true">📍</span>{ruta}
            </span>
          );
        },
      },
      {
        key: 'occupation_pct',
        label: tr('ipam.occupation', 'Ocupación'),
        sortable: true,
        sortValue: (s) => s.occupation_pct ?? 0,
        render: (s) => {
          if (!s.usable_ips) return dash;
          const pct = Math.min(100, s.occupation_pct ?? 0);
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title={`${s.used_ips} / ${s.usable_ips} IPs`}>
              <span style={{ display: 'inline-block', width: 70, height: 7, borderRadius: 4, background: 'var(--bg-toolbar)', overflow: 'hidden' }}>
                <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: occColor(pct) }} />
              </span>
              <span className="list-hint" style={{ color: occColor(pct) }}>{pct}%</span>
              <span className="list-hint">{s.used_ips}/{s.usable_ips}</span>
            </span>
          );
        },
      },
      {
        key: 'dhcp',
        label: 'DHCP',
        sortable: true,
        sortValue: (s) => (dhcpSubnetIds.has(String(s.id)) ? 1 : 0),
        render: (s) => (dhcpSubnetIds.has(String(s.id))
          ? <span className="list-badge list-badge--on">DHCP</span>
          : dash),
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (s) => s.status || 'active',
        render: (s) => {
          const meta = SUBNET_STATUS_META[s.status || 'active'] || SUBNET_STATUS_META.active;
          const full = s.usable_ips > 0 && (s.occupation_pct ?? 0) >= 100;
          return (
            <span style={{ display: 'inline-flex', gap: 4 }}>
              <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              {full && <span className="list-badge list-badge--off">Agotada</span>}
            </span>
          );
        },
      },
      {
        key: 'delegation',
        label: tr('ipam.delegation', 'Delegación'),
        sortable: true,
        sortValue: (s) => (s.viewer_is_owner === false
          ? '0-recibida'
          : s.delegated_to_company_name ? `1-${s.delegated_to_company_name}` : '2-propia'),
        render: (s) => {
          if (s.viewer_is_owner === false) {
            return <span className="list-badge list-badge--warn" title={`Delegada por empresa #${s.company_id}`}>Delegada</span>;
          }
          if (s.delegated_to_company_name) {
            return (
              <button
                type="button"
                className="list-badge list-badge--warn"
                style={{ cursor: 'pointer' }}
                title={tr('ipam.see_delegated_company', 'Ver los datos de la empresa delegada')}
                onClick={(event) => { event.stopPropagation(); showDelegatedCompany(s); }}
              >
                {s.delegated_to_company_name}
              </button>
            );
          }
          return dash;
        },
      },
      {
        key: 'description',
        label: tr('description', 'Descripción'),
        render: (s) => s.description || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, blockMap, dhcpSubnetIds, pathFor]);

  const defaultVisible = ['cidr', 'name', 'block_id', 'type', 'vlan_number', 'gateway', 'site', 'location', 'occupation_pct', 'dhcp', 'status', 'delegation'];

  const filters = [
    {
      key: 'block', label: tr('ipam.block', 'Bloque'), allLabel: tr('ipam.block_all', 'Todos los bloques'),
      value: filterBlock, onChange: setFilterBlock,
      options: blocks.map(b => ({ value: String(b.id), label: b.cidr })),
    },
    {
      key: 'version', label: tr('ipam.version', 'Versión'), allLabel: tr('ipam.version_all', 'IPv4 + IPv6'),
      value: filterVersion, onChange: setFilterVersion,
      options: [{ value: '4', label: 'IPv4' }, { value: '6', label: 'IPv6' }],
    },
    {
      key: 'type', label: tr('ipam.type', 'Tipo'), allLabel: tr('ipam.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: typeOptions.map(value => ({ value, label: subnetTypeLabel(value) })),
    },
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: allSites.map(value => ({ value, label: value })),
    },
    {
      key: 'location', label: tr('ipam.location', 'Ubicación'), allLabel: tr('ipam.location_all', 'Todas las ubicaciones'),
      value: filterLocation, onChange: setFilterLocation,
      options: [
        ...locationOptions
          .map(loc => ({ value: String(loc.id), label: pathFor(loc.id) || loc.name }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        { value: 'none', label: tr('ipam.location_none', 'Sin ubicación') },
      ],
    },
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: [
        ...Object.entries(SUBNET_STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
        { value: 'full', label: tr('ipam.status_full', 'Agotadas') },
      ],
    },
    {
      key: 'vlan', label: 'VLAN', allLabel: tr('ipam.vlan_all', 'Todas las VLANs'),
      value: filterVlan, onChange: setFilterVlan,
      options: vlans.map(v => ({
        value: String(v.id),
        label: `VLAN ${v.vlan_id || v.number} – ${v.name || '?'}`,
      })),
    },
    {
      key: 'delegation', label: tr('ipam.delegation', 'Delegación'), allLabel: tr('ipam.delegation_all', 'Todas las subredes'),
      value: filterDelegation, onChange: setFilterDelegation,
      options: [
        { value: 'mine', label: tr('ipam.delegation_mine', 'Solo las mías') },
        { value: 'out', label: tr('ipam.delegation_out', 'Delegadas a otra empresa') },
        { value: 'in', label: tr('ipam.delegation_in', 'Delegadas a mí') },
      ],
    },
    {
      key: 'occupation', label: tr('ipam.occupation', 'Ocupación'), allLabel: tr('ipam.occupation_all', 'Cualquier ocupación'),
      value: filterOccupation, onChange: setFilterOccupation,
      options: Object.entries(OCCUPATION_RANGES).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'dhcp', label: tr('ipam.only_dhcp', 'Con DHCP'),
      type: 'toggle', value: filterDhcp, onValue: true,
      onChange: (value) => setFilterDhcp(Boolean(value)),
      title: tr('ipam.only_dhcp_hint', 'Ver solo las subredes con rango DHCP'),
    },
    {
      key: 'gateway', label: tr('ipam.only_gateway', 'Con gateway'),
      type: 'toggle', value: filterGateway, onValue: true,
      onChange: (value) => setFilterGateway(Boolean(value)),
      title: tr('ipam.only_gateway_hint', 'Ver solo las subredes con puerta de enlace definida'),
    },
    {
      key: 'group', label: tr('ipam.group_site', 'Agrupar por site'),
      type: 'toggle', value: grouped, onValue: true,
      onChange: (value) => setGrouped(Boolean(value)),
    },
  ];

  const subtitle = `${subnets.length} ${tr('ipam.subnet_plural', 'subredes')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(s => ({
    'CIDR': s.cidr,
    'Nombre': s.name || '',
    'Bloque': s.block_id ? (blockMap[String(s.block_id)] || `#${s.block_id}`) : '',
    'Tipo': subnetTypeLabel(s.type),
    'Versión': `v${s.version || 4}`,
    'VLAN': s.vlan_number ?? '',
    'Gateway': s.gateway || '',
    'Site': s.site || '',
    'Ubicación': pathFor(s.location_id) || s.location_name || '',
    'IPs usables': s.usable_ips ?? '',
    'IPs usadas': s.used_ips ?? '',
    'Ocupación (%)': s.occupation_pct ?? '',
    'DHCP': dhcpSubnetIds.has(String(s.id)) ? 'Sí' : 'No',
    'Estado': (SUBNET_STATUS_META[s.status || 'active'] || SUBNET_STATUS_META.active).label,
    'Delegación': s.viewer_is_owner === false
      ? 'Delegada (recibida)'
      : (s.delegated_to_company_name ? `Delegada a ${s.delegated_to_company_name}` : ''),
    'Descripción': s.description || '',
  })), [filtered, blockMap, dhcpSubnetIds, pathFor]);

  /** Tarjetas: lo esencial de la subred, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((s) => {
          const meta = SUBNET_STATUS_META[s.status || 'active'] || SUBNET_STATUS_META.active;
          const pct = Math.min(100, s.occupation_pct ?? 0);
          return (
            <div
              key={s.id}
              id={`subnet-card-${s.id}`}
              className={`list-card${s.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setEditingSubnet(s)}
              onKeyDown={(event) => { if (event.key === 'Enter') setEditingSubnet(s); }}
              title={`${tr('action.edit', 'Editar')} ${s.cidr}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title list-code">{s.cidr}</div>
                  {s.name && <span>{s.name}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className={typeBadgeClass(s.type)}>{subnetTypeLabel(s.type)}</span>
                <span className="list-badge">v{s.version || 4}</span>
                {s.vlan_number != null && <span className="list-badge">VLAN {s.vlan_number}</span>}
                {(pathFor(s.location_id) || s.location_name) && (
                  <span>📍 {pathFor(s.location_id) || s.location_name}</span>
                )}
                {s.site && <span>🏷️ {s.site}</span>}
                {s.gateway && <span className="list-code">GW {s.gateway}</span>}
                {s.block_id && <span className="list-code">{blockMap[String(s.block_id)] || `#${s.block_id}`}</span>}
                {dhcpSubnetIds.has(String(s.id)) && <span className="list-badge list-badge--on">DHCP</span>}
                {s.viewer_is_owner === false && <span className="list-badge list-badge--warn">Delegada</span>}
                {s.delegated_to_company_name && <span className="list-badge list-badge--warn">{s.delegated_to_company_name}</span>}
              </div>

              {s.usable_ips > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 90, height: 7, borderRadius: 4, background: 'var(--bg-toolbar)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: occColor(pct) }} />
                  </span>
                  <span className="list-hint">{pct}% · {s.used_ips}/{s.usable_ips} IPs</span>
                </div>
              )}

              {s.description && <div className="list-card-meta"><span>{s.description}</span></div>}

              <div className="list-card-actions">{rowActions(s)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const SUBNET_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada subred'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, blockMap, dhcpSubnetIds, pathFor]);
  useListView('ipam_subnets', 'list');

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between">
          <span>{success}</span>
          <button className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <ListPage
        icon={ServerStackIcon}
        title={tr('ipam.subnets_title', 'Subredes')}
        subtitle={subtitle}

        onCreate={() => setWizardOpen(true)}
        createLabel={tr('ipam.subnet_new', 'Nueva subred')}
        createTitle={tr('ipam.subnet_new_hint', 'Crear una subred dentro de un bloque')}
        createDisabled={!blocks.length}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('ipam.subnet_search', 'Buscar CIDR, nombre, site, gateway…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="subredes"
        exportSheetName="Subredes"

        columns={columns}
        columnStorageKey="cmdb_cols_ipam_subnets"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(s) => s.id}
        rowId={(s) => `subnet-row-${s.id}`}
        rowClassName={(s) => (s.id === hlId ? 'list-row-highlight' : '')}
        onRowClick={setEditingSubnet}
        rowTitle={(s) => `${tr('action.edit', 'Editar')} ${s.cidr}`}
        groupBy={grouped ? groupLabelOf : undefined}

        views={SUBNET_VIEWS}
        listKey="ipam_subnets"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando subredes…')}

        /* El catálogo de tipos es transversal a la pantalla: va en la cabecera,
           junto al selector de vistas y «Nueva subred», no como acción de fila
           ni suelto encima de los filtros. */
        headerActions={(
          <button
            type="button"
            className="list-btn-secondary"
            onClick={() => setTypesOpen(true)}
            title={tr('ipam.types_hint', 'Añadir o editar los tipos de subred del catálogo')}
          >
            <Cog6ToothIcon /> {tr('ipam.types', 'Tipos de subred')}
          </button>
        )}

        empty={{
          icon: '🧩',
          title: subnets.length === 0
            ? tr('ipam.subnet_empty', 'No hay subredes')
            : tr('msg.no_results', 'Ninguna subred coincide con los filtros'),
          subtitle: subnets.length === 0
            ? tr('ipam.subnet_empty_hint', 'Crea primero un bloque y después sus subredes.')
            : undefined,
          action: subnets.length === 0 ? (
            <button
              type="button"
              className="list-btn-primary"
              onClick={() => setWizardOpen(true)}
              disabled={!blocks.length}
            >
              ＋ {tr('ipam.subnet_new', 'Nueva subred')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (s) => (
            <>
              {/* Explorar: mapa de IPs de la subred (solo si la vemos como dueños) */}
              <button
                type="button"
                className="list-action-btn"
                disabled={s.viewer_is_owner === false}
                onClick={() => navigate(`/networks/explorer?blockId=${s.block_id}&subnetId=${s.id}`)}
                title={tr('ipam.subnet_explore', 'Explorar el mapa de IPs de la subred')}
              >
                <MagnifyingGlassIcon />
              </button>
              {/* Ver rango DHCP: salta a la pestaña de DHCP filtrada por subred */}
              <button
                type="button"
                className="list-action-btn"
                disabled={!dhcpSubnetIds.has(String(s.id))}
                onClick={() => navigate(`/networks/dhcp?subnetId=${s.id}`)}
                title={tr('ipam.subnet_dhcp', 'Ver el rango DHCP de esta subred')}
              >
                <ServerStackIcon />
              </button>
              {/* Ver empresa delegada */}
              <button
                type="button"
                className="list-action-btn"
                disabled={!s.delegated_to_company_id}
                onClick={() => showDelegatedCompany(s)}
                title={tr('ipam.see_delegated_company', 'Ver los datos de la empresa delegada')}
              >
                <BuildingOffice2Icon />
              </button>
            </>
          ),
          onCopy: openCopy,
          onEdit: setEditingSubnet,
          canEdit: (s) => s.viewer_is_owner !== false,
          canDelete: (s) => s.viewer_is_owner !== false,
          onDelete: handleDeleteSubnet,
          deleteTitle: tr('ipam.subnet_delete_title', 'Eliminar subred'),
          deleteMessage: (s) => (
            <>
              {tr('ipam.subnet_delete_confirm', 'Vas a eliminar la subred')} <strong>{s.cidr}</strong>
              {s.name ? ` (${s.name})` : ''}.{' '}
              {tr('ipam.subnet_delete_hint', 'Antes de borrar se mostrará todo lo que arrastra (IPs asignadas, elementos que se quedarán sin red). No se puede deshacer.')}
            </>
          ),
        }}
      />

      {wizardOpen && (
        <SubnetWizard
          companyId={companyId}
          blocks={blocks}
          vlans={vlans}
          companies={companies}
          subnetTypes={subnetTypes}
          locations={locationOptions}
          initialBlockId={filterBlock}
          initial={wizardInitial}
          onCreated={() => { load(); setWizardOpen(false); setWizardInitial(null); }}
          onClose={() => { setWizardOpen(false); setWizardInitial(null); }}
          onVlanCreated={handleVlanCreated}
        />
      )}

      {editingSubnet && (
        <SubnetEditModal
          subnet={editingSubnet}
          vlans={vlans}
          companies={companies}
          companyId={companyId}
          saving={editSaving}
          subnetTypes={subnetTypes}
          locations={locationOptions}
          onSave={handleUpdateSubnet}
          onClose={() => setEditingSubnet(null)}
          onVlanCreated={handleVlanCreated}
        />
      )}

      {typesOpen && (
        <IpamSubnetTypesManager
          companyId={companyId}
          types={subnetTypes}
          onChanged={async () => { await reloadSubnetTypes(); load(); }}
          onClose={() => setTypesOpen(false)}
        />
      )}

      {/* Ficha mínima de la empresa a la que se delegó la subred */}
      {delegatedCompany && (
        <div
          className="modal d-block"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
          onClick={(event) => { if (event.target === event.currentTarget) setDelegatedCompany(null); }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header py-3">
                <h6 className="modal-title fw-semibold mb-0">
                  {tr('ipam.delegated_company', 'Empresa delegada')}
                </h6>
                <button className="btn-close" onClick={() => setDelegatedCompany(null)} />
              </div>
              <div className="modal-body">
                <dl className="mb-0" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem' }}>
                  <dt className="list-hint mb-0">{tr('ipam.company', 'Empresa')}</dt>
                  <dd className="mb-0" style={{ fontWeight: 600 }}>{delegatedCompany.name}</dd>
                  <dt className="list-hint mb-0">{tr('ipam.company_id', 'Identificador')}</dt>
                  <dd className="mb-0 list-code">#{delegatedCompany.id}</dd>
                  {delegatedCompany.slug && (
                    <>
                      <dt className="list-hint mb-0">Slug</dt>
                      <dd className="mb-0 list-code">{delegatedCompany.slug}</dd>
                    </>
                  )}
                  {delegatedCompany.domain && (
                    <>
                      <dt className="list-hint mb-0">Dominio</dt>
                      <dd className="mb-0">{delegatedCompany.domain}</dd>
                    </>
                  )}
                  <dt className="list-hint mb-0">{tr('ipam.subnet', 'Subred')}</dt>
                  <dd className="mb-0 list-code">{delegatedCompany.subnet}</dd>
                </dl>
              </div>
              <div className="modal-footer py-2">
                <span className="list-hint me-auto">
                  {tr('ipam.delegation_readonly', 'Esa empresa ve esta subred en modo lectura.')}
                </span>
                <button type="button" className="list-btn-secondary" onClick={() => setDelegatedCompany(null)}>
                  {tr('action.close', 'Cerrar')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <IpamCascadeDeleteModal
          title={`Eliminar subred ${deleteTarget.subnet.cidr}`}
          subtitle={deleteTarget.subnet.name || ''}
          preview={deleteTarget.preview}
          busy={deleting}
          error={deleteError}
          onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(''); } }}
          onConfirm={confirmDeleteSubnet}
        />
      )}
    </IPAMLayout>
  );
}

/* ── Subnet edit modal ───────────────────────────────────────────────────── */
function SubnetEditModal({ subnet, vlans, companies, companyId, saving, onSave, onClose, onVlanCreated, subnetTypes = [], locations = [] }) {
  const [form, setForm] = useState({
    type:                    subnet.type                    || '',
    vlan_id:                 subnet.vlan_id                 != null ? String(subnet.vlan_id) : '',
    gateway:                 subnet.gateway                 || '',
    site:                    subnet.site                    || '',
    location_id:             subnet.location_id             != null ? String(subnet.location_id) : '',
    name:                    subnet.name                    || '',
    description:             subnet.description             || '',
    status:                  subnet.status                  || 'active',
    delegated_to_company_id: subnet.delegated_to_company_id != null ? String(subnet.delegated_to_company_id) : '',
  });
  const [showVlanCreate, setShowVlanCreate] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...form };
    if (!data.vlan_id) delete data.vlan_id;
    else data.vlan_id = Number(data.vlan_id);
    data.location_id = data.location_id ? Number(data.location_id) : null;
    // delegation
    if (!data.delegated_to_company_id) data.delegated_to_company_id = null;
    else data.delegated_to_company_id = Number(data.delegated_to_company_id);
    onSave(data);
  };

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title fw-semibold mb-0">
              Editar subred <code className="ms-1">{subnet.cidr}</code>
            </h6>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {/* Tipo */}
              <div>
                <label className="form-label small mb-1">Tipo</label>
                <select className="form-select form-select-sm" value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {subnetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {/* VLAN */}
              <div>
                <label className="form-label small mb-1">VLAN</label>
                <div className="d-flex gap-1">
                  <select className="form-select form-select-sm" value={form.vlan_id}
                    onChange={e => setForm(f => ({ ...f, vlan_id: e.target.value }))}>
                    <option value="">Sin VLAN</option>
                    {vlans.map(v => <option key={v.id} value={v.id}>VLAN {v.vlan_id || v.number} – {v.name || '?'}{v.site ? ` (${v.site})` : ''}</option>)}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm text-nowrap"
                    title="Crear una VLAN nueva sin salir de aquí"
                    onClick={() => setShowVlanCreate(true)}
                  >＋ VLAN</button>
                </div>
              </div>
              {/* Gateway */}
              <div>
                <label className="form-label small mb-1">Gateway</label>
                <input className="form-control form-control-sm" placeholder="ej. 10.0.1.1"
                  value={form.gateway}
                  onChange={e => setForm(f => ({ ...f, gateway: e.target.value }))} />
              </div>
              {/* Site */}
              <div>
                <label className="form-label small mb-1">Site</label>
                <input className="form-control form-control-sm" placeholder="ej. MAD-DC1"
                  value={form.site}
                  onChange={e => setForm(f => ({ ...f, site: e.target.value }))} />
              </div>
              {/* Ubicación física: los equipos con IP en esta subred se colocan aquí */}
              <div>
                <label className="form-label small mb-1">Ubicación física</label>
                <LocationTreeSelect
                  locations={locations}
                  value={form.location_id}
                  onChange={(value) => setForm(f => ({ ...f, location_id: value }))}
                  placeholder="Sin ubicación vinculada"
                  extraOptions={[{ value: '', label: 'Sin ubicación vinculada' }]}
                />
                <div className="form-text text-muted" style={{ fontSize: '0.76em' }}>
                  Los equipos cuya IP caiga en esta subred se moverán a esa ubicación.
                </div>
              </div>
              {/* Status */}
              <div>
                <label className="form-label small mb-1">Estado</label>
                <select className="form-select form-select-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">active</option>
                  <option value="reserved">reserved</option>
                  <option value="deprecated">deprecated</option>
                </select>
              </div>
              {/* Name */}
              <div>
                <label className="form-label small mb-1">Nombre / Código</label>
                <input className="form-control form-control-sm" placeholder="ej. PROD-LAN-01"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              {/* Description */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label small mb-1">Descripción</label>
                <input className="form-control form-control-sm" placeholder="Descripción opcional"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              {/* Delegation */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label small mb-1">🔗 Delegar a empresa <span className="text-muted fw-normal">(opcional)</span></label>
                <select className="form-select form-select-sm"
                  value={form.delegated_to_company_id}
                  onChange={e => setForm(f => ({ ...f, delegated_to_company_id: e.target.value }))}>
                  <option value="">Sin delegación</option>
                  {companies
                    .filter(c => String(c.id) !== String(companyId))
                    .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="form-text text-muted" style={{ fontSize: '0.76em' }}>
                  La empresa seleccionada verá esta subred de forma de sólo lectura. El bloque padre siempre permanecerá oculto.
                </div>
              </div>
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showVlanCreate && (
        <IpamVlanQuickCreate
          companyId={companyId}
          vlans={vlans}
          defaultSite={form.site}
          onClose={() => setShowVlanCreate(false)}
          onCreated={(vlan) => {
            onVlanCreated?.(vlan);
            if (vlan?.id != null) setForm(f => ({ ...f, vlan_id: String(vlan.id) }));
            setShowVlanCreate(false);
          }}
        />
      )}
    </div>
  );
}

/* ── Subnet creation wizard ─────────────────────────────────────────────────
   4-step modal:
     1. Select block
     2. Choose prefix/mask  →  free-space table from /blocks/<id>/free-space
     3. Choose CIDR         →  suggestions from /blocks/<id>/suggest-subnet
     4. Set type, VLAN, site, description
────────────────────────────────────────────────────────────────────────────── */
function SubnetWizard({ companyId, blocks, vlans, companies: companiesProp, initialBlockId, initial = null, onCreated, onClose, onVlanCreated, subnetTypes = [], locations = [] }) {
  const STEPS = ['Bloque', 'Máscara', 'CIDR', 'Detalles'];
  /* `initial` se rellena al COPIAR una subred: se precargan bloque, tipo, VLAN,
     site, nombre y delegación, y se salta al paso de la máscara; el CIDR es lo
     único que hay que elegir de nuevo. */
  const [step, setStep] = useState(initial?.blockId ? 1 : 0);

  const [blockId, setBlockId]         = useState(initial?.blockId || initialBlockId || '');
  const [freeSpace, setFreeSpace]     = useState([]);
  const [loadingFS, setLoadingFS]     = useState(false);
  const [selPrefix, setSelPrefix]     = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSug, setLoadingSug]   = useState(false);
  const [validatingManual, setValidatingManual] = useState(false);
  const [selectionMode, setSelectionMode] = useState('suggested');
  const [manualCidr, setManualCidr] = useState('');
  const [selCidr, setSelCidr]         = useState('');
  const [gateway, setGateway]         = useState('');
  const [subnetType, setSubnetType]   = useState(initial?.type || 'lan');
  const [vlanId, setVlanId]           = useState(initial?.vlanId || '');
  const [showVlanCreate, setShowVlanCreate] = useState(false);
  const [site, setSite]               = useState(initial?.site || '');
  const [locationId, setLocationId]   = useState(initial?.locationId || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [name, setName]               = useState(initial?.name || '');
  const [delegateTo, setDelegateTo]   = useState(initial?.delegateTo || ''); // delegated_to_company_id
  const [companies, setCompanies]     = useState(companiesProp || []);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  // Load companies independently so the dropdown always has data
  useEffect(() => {
    if (companies.length === 0) {
      api.getAuthorizedCompanies().then(setCompanies).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selBlock = blocks.find(b => String(b.id) === String(blockId));
  const busy     = loadingFS || loadingSug || validatingManual || saving;

  const validateManualSelection = async () => {
    const candidate = manualCidr.trim();
    if (!candidate) {
      setError('Introduce un CIDR manual para validarlo.');
      return false;
    }

    setValidatingManual(true);
    setError('');
    try {
      const res = await api.validateIpamSubnetInBlock(blockId, candidate, companyId);
      const data = res.data || {};
      setSelCidr(data.cidr || candidate);
      setManualCidr(data.cidr || candidate);
      setGateway(data.gateway_recommended || '');
      return true;
    } catch (e) {
      setError(e.message || 'Error validando la subred manual');
      return false;
    } finally {
      setValidatingManual(false);
    }
  };

  const goNext = async () => {
    setError('');
    if (step === 0) {
      setLoadingFS(true);
      try {
        const res = await api.getIpamBlockFreeSpace(blockId, companyId);
        setFreeSpace(res.data || []);
        setSelPrefix(null);
        setStep(1);
      } catch (e) { setError(e.message); }
      finally { setLoadingFS(false); }
    } else if (step === 1) {
      setLoadingSug(true);
      try {
        const res = await api.getIpamBlockSuggestSubnet(blockId, selPrefix, companyId);
        const sugs = res.data || [];
        setSuggestions(sugs);
        setSelectionMode(sugs.length > 0 ? 'suggested' : 'manual');
        if (sugs.length > 0) {
          setSelCidr(sugs[0].cidr);
          setManualCidr(sugs[0].cidr);
          setGateway(sugs[0].gateway_recommended || '');
        } else {
          setSelCidr('');
          setManualCidr('');
          setGateway('');
        }
        setStep(2);
      } catch (e) { setError(e.message); }
      finally { setLoadingSug(false); }
    } else if (step === 2) {
      if (selectionMode === 'manual') {
        const ok = await validateManualSelection();
        if (!ok) return;
      }
      setStep(3);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.createIpamSubnet({
        block_id:               Number(blockId),
        cidr:                   selCidr,
        name:                   name                 || undefined,
        gateway:                gateway              || undefined,
        type:                   subnetType,
        vlan_id:                vlanId               || undefined,
        site:                   site                 || undefined,
        location_id:            locationId ? Number(locationId) : null,
        description:            description          || undefined,
        delegated_to_company_id: delegateTo ? Number(delegateTo) : undefined,
      }, companyId);
      onCreated();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const occBadgeClass = (pct) =>
    pct >= 90 ? 'bg-danger' : pct >= 70 ? 'bg-warning' : 'bg-success';

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 660 }}>
        <div className="modal-content shadow-lg">

          {/* Header */}
          <div className="modal-header py-3 bg-primary bg-opacity-5 border-bottom">
            <div>
              <h6 className="modal-title fw-bold mb-0">Asistente de nueva subred</h6>
              <div className="text-muted" style={{ fontSize: '0.78em' }}>
                Sigue los pasos — el asistente calcula los rangos disponibles automáticamente
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Step indicator */}
          <div className="px-4 pt-3 pb-1">
            <div className="d-flex align-items-center gap-1">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div
                    className={`d-flex align-items-center gap-1 ${i < step ? 'text-success' : i === step ? 'text-primary fw-semibold' : 'text-muted opacity-50'}`}
                    style={{ fontSize: '0.82em', cursor: i < step ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                    onClick={() => { if (i < step) { setStep(i); setError(''); } }}
                  >
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%', fontSize: '0.72em', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      background: i < step ? '#22c55e' : i === step ? '#0d6efd' : '#dee2e6',
                      color: i <= step ? 'white' : '#888',
                      transition: 'background .15s',
                    }}>{i < step ? '✓' : i + 1}</div>
                    {s}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div style={{
                      flex: 1, height: 2, minWidth: 16, borderRadius: 2, transition: 'background .15s',
                      background: i < step ? '#22c55e' : '#dee2e6',
                    }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="modal-body px-4 pb-2" style={{ minHeight: 260 }}>
            {error && (
              <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.88em' }}>{error}</div>
            )}

            {/* ── STEP 1: Block ── */}
            {step === 0 && (
              <div>
                <p className="text-muted small mb-3">Selecciona el bloque de IPs donde quieres crear la nueva subred.</p>
                <div className="d-flex flex-column gap-2">
                  {blocks.map(b => (
                    <div key={b.id}
                      className={`border rounded p-3 ${String(blockId) === String(b.id) ? 'border-primary border-2 bg-primary bg-opacity-5' : ''}`}
                      style={{ cursor: 'pointer', transition: 'all .1s' }}
                      onClick={() => setBlockId(String(b.id))}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.05em' }}>{b.cidr}</span>
                          {b.description && <span className="text-muted ms-2" style={{ fontSize: '0.82em' }}>{b.description}</span>}
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          {b.occupation_pct != null && (
                            <span className={`badge ${occBadgeClass(b.occupation_pct)}`} style={{ fontSize: '0.72em' }}>
                              {b.occupation_pct}% usado
                            </span>
                          )}
                          {b.type && <span className="badge bg-secondary bg-opacity-75" style={{ fontSize: '0.7em' }}>{b.type.toUpperCase()}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  {blocks.length === 0 && (
                    <p className="text-muted small">No hay bloques. Crea un bloque primero en la pestaña Bloques.</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2: Prefix/mask ── */}
            {step === 1 && (
              <div>
                <p className="text-muted small mb-3">
                  Bloque: <strong className="font-monospace">{selBlock?.cidr}</strong>
                  &ensp;·&ensp;Elige el tamaño (máscara) para la nueva subred.
                </p>
                <div className="table-responsive" style={{ borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <table className="table table-sm align-middle mb-0" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                    <thead style={{ background: 'var(--slate-200)', color: 'var(--text-secondary)', fontSize: '0.79em', borderBottom: '1px solid var(--border)' }}>
                      <tr>
                        <th>Prefijo</th>
                        <th className="text-end">IPs totales</th>
                        <th className="text-end">Hosts usables</th>
                        <th className="text-end">Slots libres</th>
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: '0.86em' }}>
                      {freeSpace.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-3">Sin datos de espacio disponible.</td></tr>
                      )}
                      {freeSpace.map(row => {
                        const sel   = selPrefix === row.prefix;
                        const empty = row.available_slots === 0;
                        return (
                          <tr key={row.prefix}
                            style={{
                              cursor: empty ? 'not-allowed' : 'pointer',
                              background: sel ? 'rgba(var(--accent-rgb), 0.15)' : 'transparent',
                              opacity: empty ? 0.45 : 1,
                              borderBottom: '1px solid var(--border)',
                            }}
                            onMouseEnter={e => { if (!sel && !empty) e.currentTarget.style.background = 'var(--slate-200)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = sel ? 'rgba(var(--accent-rgb), 0.15)' : 'transparent'; }}
                            onClick={() => { if (!empty) setSelPrefix(row.prefix); }}
                          >
                            <td>
                              <span className="font-monospace fw-bold" style={{ color: 'var(--text-primary)' }}>{row.cidr_notation}</span>
                              {sel && <span className="badge ms-2" style={{ fontSize: '0.7em', background: 'rgba(var(--accent-rgb), 0.25)', color: 'var(--accent)' }}>Seleccionado</span>}
                            </td>
                            <td className="text-end font-monospace" style={{ color: 'var(--text-secondary)' }}>{row.ips_per_subnet.toLocaleString()}</td>
                            <td className="text-end">
                              <span className="fw-semibold" style={{ color: empty ? 'var(--text-muted)' : '#16a34a' }}>
                                {row.usable_ips.toLocaleString()}
                              </span>
                            </td>
                            <td className="text-end">
                              {empty
                                ? <span style={{ fontSize: '0.82em', color: '#dc2626' }}>Sin espacio</span>
                                : <span className="fw-semibold" style={{ color: '#16a34a' }}>
                                    {row.available_slots}{row.capped ? '+' : ''}
                                  </span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {selPrefix && (
                  <div className="d-flex align-items-center gap-2 mt-3 mb-0 px-3 py-2" style={{ fontSize: '0.85em', background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '6px', color: 'var(--text-primary)' }}>
                    <span>✓</span>
                    <span>
                      <strong>/{selPrefix}</strong>
                      {' '}→ {freeSpace.find(r => r.prefix === selPrefix)?.usable_ips.toLocaleString()} hosts por subred
                      &ensp;·&ensp;Gateway recomendado: primera IP del rango (e.g. .1)
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3: CIDR selection ── */}
            {step === 2 && (
              <div>
                <p className="text-muted small mb-3">
                  Rangos <strong>/{selPrefix}</strong> disponibles en <strong className="font-monospace">{selBlock?.cidr}</strong>.
                  Puedes elegir una recomendación o introducir un CIDR manual para validarlo dentro del bloque.
                </p>
                <div className="d-flex gap-1 mb-3">
                  <button
                    type="button"
                    style={{
                      padding: '4px 14px', fontSize: '0.82em', borderRadius: '6px', border: '1px solid var(--border)',
                      background: selectionMode === 'suggested' ? 'var(--accent)' : 'transparent',
                      color: selectionMode === 'suggested' ? '#fff' : 'var(--text-secondary)',
                      cursor: suggestions.length === 0 ? 'not-allowed' : 'pointer', opacity: suggestions.length === 0 ? 0.45 : 1,
                    }}
                    onClick={() => setSelectionMode('suggested')}
                    disabled={suggestions.length === 0}
                  >
                    Recomendadas
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '4px 14px', fontSize: '0.82em', borderRadius: '6px', border: '1px solid var(--border)',
                      background: selectionMode === 'manual' ? 'var(--accent)' : 'transparent',
                      color: selectionMode === 'manual' ? '#fff' : 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectionMode('manual')}
                  >
                    Manual
                  </button>
                </div>

                {selectionMode === 'suggested' && (
                  suggestions.length === 0 ? (
                    <div className="py-2 px-3 mb-3" style={{ background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '0.86em' }}>
                      No se encontraron rangos libres para este prefijo en el bloque seleccionado. Usa el modo manual.
                    </div>
                  ) : (
                    <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: '1rem' }}>
                      {suggestions.map((sug, i) => {
                        const sel = selCidr === sug.cidr;
                        return (
                          <div key={sug.cidr}
                            style={{
                              cursor: 'pointer', transition: 'background .1s',
                              background: sel ? 'rgba(var(--accent-rgb), 0.15)' : 'var(--bg-card)',
                              border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                              borderRadius: '6px', padding: '8px 12px', marginBottom: '8px',
                            }}
                            onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'var(--slate-200)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = sel ? 'rgba(var(--accent-rgb), 0.15)' : 'var(--bg-card)'; }}
                            onClick={() => {
                              setSelCidr(sug.cidr);
                              setManualCidr(sug.cidr);
                              setGateway(sug.gateway_recommended || '');
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="font-monospace fw-bold" style={{ color: 'var(--text-primary)' }}>{sug.cidr}</span>
                              <div className="d-flex gap-1">
                                {i === 0 && <span className="badge" style={{ fontSize: '0.7em', background: 'rgba(22,163,74,0.18)', color: '#16a34a' }}>Recomendado</span>}
                                {sel && i > 0 && <span className="badge" style={{ fontSize: '0.7em', background: 'rgba(var(--accent-rgb),0.2)', color: 'var(--accent)' }}>Seleccionado</span>}
                                <span className="badge" style={{ fontSize: '0.7em', background: 'var(--slate-200)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
                                  {sug.usable_ips} hosts
                                </span>
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-3 mt-1" style={{ fontSize: '0.77em', color: 'var(--text-muted)' }}>
                              <span>Red: <code style={{ color: 'var(--text-secondary)' }}>{sug.network}</code></span>
                              <span>Broadcast: <code style={{ color: 'var(--text-secondary)' }}>{sug.broadcast}</code></span>
                              <span style={{ color: '#16a34a' }} className="fw-semibold">GW sugerido: <code>{sug.gateway_recommended}</code></span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {selectionMode === 'manual' && (
                  <div className="p-3 mb-3" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <label className="form-label small fw-semibold mb-1" style={{ color: 'var(--text-primary)' }}>CIDR manual</label>
                    <div className="d-flex gap-2 align-items-start">
                      <input
                        className="form-control form-control-sm font-monospace"
                        placeholder="Ej. 10.1.0.0/24"
                        value={manualCidr}
                        onChange={e => {
                          setManualCidr(e.target.value);
                          setSelCidr('');
                        }}
                        onKeyDown={async e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            await validateManualSelection();
                          }
                        }}
                      />
                      <button
                        type="button"
                        style={{
                          padding: '4px 14px', fontSize: '0.82em', borderRadius: '6px', whiteSpace: 'nowrap',
                          border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', cursor: 'pointer',
                          opacity: (!manualCidr.trim() || validatingManual) ? 0.5 : 1,
                        }}
                        onClick={validateManualSelection}
                        disabled={!manualCidr.trim() || validatingManual}
                      >
                        {validatingManual ? 'Validando…' : 'Validar'}
                      </button>
                    </div>
                    <div style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginTop: '6px' }}>
                      Se comprobará que la subred pertenece al bloque, que es hija del prefijo elegido y que no solapa con otra existente.
                    </div>
                    {selCidr && (
                      <div className="d-flex align-items-center gap-2 mt-3 px-3 py-2" style={{ fontSize: '0.84em', background: 'rgba(22,163,74,0.12)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '6px', color: 'var(--text-primary)' }}>
                        CIDR válido: <strong className="font-monospace">{selCidr}</strong>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="form-label small fw-semibold mb-1">
                    Puerta de enlace (Gateway)
                    <span className="text-muted fw-normal ms-2">— autocompletada, puedes modificarla</span>
                  </label>
                  <input className="form-control form-control-sm font-monospace" placeholder="10.0.1.1"
                    value={gateway} onChange={e => setGateway(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        await goNext();
                      }
                    }} />
                </div>
              </div>
            )}

            {/* ── STEP 4: Details ── */}
            {step === 3 && (
              <form id="wizard-details-form" onSubmit={create}>
                <div className="p-3 border rounded mb-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}>
                  <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.88em' }}>
                    <div><span className="text-muted">CIDR:</span> <strong className="font-monospace">{selCidr}</strong></div>
                    <div><span className="text-muted">Bloque:</span> <strong className="font-monospace">{selBlock?.cidr}</strong></div>
                    {gateway && <div><span className="text-muted">Gateway:</span> <strong className="font-monospace">{gateway}</strong></div>}
                  </div>
                </div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Tipo *</label>
                    <select className="form-select form-select-sm" value={subnetType} onChange={e => setSubnetType(e.target.value)}>
                      {subnetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">VLAN</label>
                    <div className="d-flex gap-1">
                      <select className="form-select form-select-sm" value={vlanId} onChange={e => setVlanId(e.target.value)}>
                        <option value="">Sin VLAN</option>
                        {vlans.map(v => <option key={v.id} value={v.id}>VLAN {v.vlan_id || v.number} – {v.name || '?'}{v.site ? ` (${v.site})` : ''}</option>)}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm text-nowrap"
                        title="Crear una VLAN nueva sin salir del asistente"
                        onClick={() => setShowVlanCreate(true)}
                      >＋ VLAN</button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Site / Localización</label>
                    <input className="form-control form-control-sm" placeholder="datacenter-1…"
                      value={site} onChange={e => setSite(e.target.value)} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold mb-1">Ubicación física <span className="text-muted fw-normal">(opcional)</span></label>
                    <LocationTreeSelect
                      locations={locations}
                      value={locationId}
                      onChange={setLocationId}
                      placeholder="Sin ubicación vinculada"
                      extraOptions={[{ value: '', label: 'Sin ubicación vinculada' }]}
                    />
                    <div className="form-text text-muted" style={{ fontSize: '0.76em' }}>
                      Los equipos cuya IP caiga en esta subred pasarán a esa ubicación (y se moverán cuando el agente detecte otra IP).
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Nombre / Código</label>
                    <input className="form-control form-control-sm" placeholder="PROD-LAN-01…"
                      value={name} onChange={e => setName(e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold mb-1">Descripción</label>
                    <textarea className="form-control form-control-sm" rows={2} placeholder="Propósito de la subred…"
                      value={description} onChange={e => setDescription(e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold mb-1">🔗 Delegar a empresa <span className="text-muted fw-normal">(opcional)</span></label>
                    <select className="form-select form-select-sm" value={delegateTo} onChange={e => setDelegateTo(e.target.value)}>
                      <option value="">Sin delegación</option>
                      {companies
                        .filter(c => String(c.id) !== String(companyId))
                        .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <div className="form-text text-muted" style={{ fontSize: '0.76em' }}>
                      La empresa elegida verá esta subred en su IPAM de sólo lectura. El bloque padre no será visible.
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer py-2 d-flex justify-content-between align-items-center">
            <div>
              {step > 0 && (
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => { setStep(s => s - 1); setError(''); }}
                  disabled={busy}
                >← Atrás</button>
              )}
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={busy}>Cancelar</button>
              {step === 0 && (
                <button type="button" className="btn btn-primary btn-sm" onClick={goNext} disabled={!blockId || busy}>
                  {busy ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Calculando…</> : 'Siguiente →'}
                </button>
              )}
              {step === 1 && (
                <button type="button" className="btn btn-primary btn-sm" onClick={goNext} disabled={selPrefix === null || busy}>
                  {busy ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Calculando…</> : 'Siguiente →'}
                </button>
              )}
              {step === 2 && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={selectionMode === 'manual' ? !manualCidr.trim() || busy : !selCidr || busy}
                  onClick={goNext}
                >
                  {validatingManual ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Validando…</> : 'Siguiente →'}
                </button>
              )}
              {step === 3 && (
                <button className="btn btn-success btn-sm" type="submit" form="wizard-details-form" disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Creando…</> : '✓ Crear subred'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showVlanCreate && (
        <IpamVlanQuickCreate
          companyId={companyId}
          vlans={vlans}
          defaultSite={site}
          onClose={() => setShowVlanCreate(false)}
          onCreated={(vlan) => {
            onVlanCreated?.(vlan);
            if (vlan?.id != null) setVlanId(String(vlan.id));
            setShowVlanCreate(false);
          }}
        />
      )}
    </div>
  );
}
